import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { AlertCircle, ArrowLeft, Save, EyeOff, Type, Plus, Trash2, Copy, Crosshair, Check, Lock, Shield, Layers, Search, Users, AlertTriangle } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import './OhipTransactionsPage.css';
import './UniversalApiPage.css';
import './PaymentPorterProcessorPage.css';
import { apiErrorMessage } from '../utils/apiError';

// Admin > Platform > Universal API > Routes and Payloads > Redactions.
//
// A REDACTION IS A NAMED SET OF RULES, and a payload has several. Two consumers of the same call can be
// given different views of it: a channel manager may not see the guest's passport number, the property's
// own reporting tool must. Rules attached straight to the payload gave one answer per field for
// everybody, which cannot express that.
//
// EVERY PAYLOAD HAS A DEFAULT that redacts nothing. It is what a consumer gets when nobody has decided
// otherwise, which is what stops this feature changing the documents existing consumers already receive.
// It is locked - no rules, no rename, no delete - because a baseline that could be edited would stop
// being one the moment it was, and every consumer relying on the absence of a decision would change at
// once with nothing naming the change.
//
// THE FIELD IS PICKED BY CLICKING THE DOCUMENT, not typed. A reservation body is hundreds of fields deep
// and the same leaf name appears in several places, so a typed path is a guess that fails silently.
//
// PATHS ARE SHAPES, NOT POSITIONS. Array indices collapse to `[]`, so `reservations[].guest.lastName`
// covers every reservation in the response and not merely the first.

const MODES = {
    NODE: { label: 'Whole node', hint: 'The key is removed. The consumer cannot tell it was ever there.' },
    TEXT: { label: 'Text only', hint: 'The key stays, the value is replaced. The consumer sees it was withheld.' },
};

const DEFAULT_REPLACEMENT = '***';

// WHO A REDACTION IS FOR (V437).
//
// A redaction covers any number of AUDIENCES, and each one is a navbar selection: a partner, optionally a
// chain within it, optionally a hotel within that, optionally one x-hotelid of that hotel. Not all four
// are needed — a partner and a chain means every hotel in that chain.
//
// Of every audience that matches a caller the narrowest wins — x-hotelid, then hotel, then chain, then
// partner — and the payload's default applies when none of them names them. The server holds that order
// (db.REDACTION_LEVELS) and resolves against it; this screen only has to say which level a scope is.
//
// What a scope covers, and the trail it was chosen by. Names come from the server with the scope — the
// ids are the reference, so a renamed chain reads correctly with nothing written here.
const scopeReads = (sc) => {
    if (sc.uapi_access_id) {
        return { level: 'x-hotelid', name: sc.x_hotelid,
                 trail: [sc.partner_name, sc.account_name, sc.hotel_name, sc.access_system_name] };
    }
    if (sc.hotel_id) {
        return { level: 'Hotel', name: sc.hotel_name, trail: [sc.partner_name, sc.account_name] };
    }
    if (sc.account_id) {
        return { level: 'Chain', name: sc.account_name, trail: [sc.partner_name],
                 covers: 'every hotel in the chain' };
    }
    return { level: 'Partner', name: sc.partner_name, trail: [],
             covers: 'every chain and hotel under it' };
};

// A local id, so a rule can be edited before it has a path or a database row. Not Date.now(): two clicks
// in the same millisecond would collide and React would reuse one card's DOM for another.
let nextKey = 1;
const newRule = () => ({ key: `new-${nextKey++}`, name: '', json_path: '', mode: 'NODE',
                        replacement: null, scope: 'PATH' });

function UapiPayloadRedactionsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { selectedAccount, selectedHotel } = useAccount();

    // BACK GOES WHERE YOU CAME FROM, not to this screen's usual parent. It is reached from two places —
    // Admin > Payloads, and Universal API > Redactions — and hardcoding the first sent anybody arriving
    // from the second into an admin screen they had not been on.
    //
    // A NAMED ORIGIN, not history.back(): a reload, a link shared with somebody, or a save that replaced
    // the entry all break a history step, and the answer then is silently wrong rather than absent.
    const ORIGINS = {
        'uapi-redactions': '/universal-api/redactions',
        payloads: '/admin/universal-api/payloads',
    };
    const from = new URLSearchParams(window.location.search).get('from');

    // TWO SCREENS, ONE EDITOR.
    //
    //   Admin > Payloads   — authors the PLATFORM's redactions. Only system sets are listed, and they
    //                        are editable. Somebody else's chain-owned set has no business being
    //                        editable from a screen with no chain on it.
    //   Universal API      — works inside a chain and a property. System sets are shown READ-ONLY, as
    //                        the baseline to depart from, alongside this chain's and this hotel's own,
    //                        which are theirs to edit.
    //
    // Told apart by where the reader came from, not by their permissions: a superadmin is both people
    // at different moments, and which one they are is which screen they opened.
    const propertyMode = from === 'uapi-redactions';


    const [payload, setPayload] = useState(null);
    const [sets, setSets] = useState([]);
    const [activeSetId, setActiveSetId] = useState(null);
    const [rules, setRules] = useState([]);        // the working list for the active set
    const [saved, setSaved] = useState([]);        // what is stored for it, for the dirty check
    const [picking, setPicking] = useState(null);
    // A set can hold dozens of rules and they are all the same size on screen, so finding one meant
    // reading every card. Collapsed to a line each, searchable, and one open at a time.
    const [ruleSearch, setRuleSearch] = useState('');
    const [openRule, setOpenRule] = useState(null);
    // The redactions themselves are a LIST, not a tab strip. A payload can carry one per consumer, so a
    // strip of pills stops being readable at about six and there is nowhere to show what each one is for
    // or how big it is. List first, click one to open it.
    const [setSearch, setSetSearch] = useState('');
    const [view, setView] = useState('list');
    // The audiences a redaction can be addressed to. Loaded once; the picker needs all four levels.
    // Where the OPEN shape rule actually lands, keyed by "type.path" so switching rules cannot show the
    // previous rule's answer while the new one is in flight.
    const [coverage, setCoverage] = useState({});
    const [targets, setTargets] = useState(null);
    const [scopes, setScopes] = useState([]);            // every scope on every set of this payload
    // The selection being built, narrowing left to right. Held apart from the saved scopes: a partly
    // made choice is not an audience yet.
    const [draft, setDraft] = useState({ partner_id: '', account_id: '', hotel_id: '', uapi_access_id: '' });
    const [savingScope, setSavingScope] = useState(false);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState('');
    const [deleting, setDeleting] = useState(null);
    // The set a copy is being made of, so its own button can say so rather than the whole list.
    const [duplicating, setDuplicating] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [confirmLeave, setConfirmLeave] = useState(null);

    const errText = (e, fallback) =>
        e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

    const toRules = (all, setId) => (all || [])
        .filter(r => String(r.redaction_set_id) === String(setId))
        .map(r => ({ key: `db-${r.id}`, name: r.name || '', json_path: r.json_path,
                     mode: r.mode, replacement: r.replacement, scope: r.scope || 'PATH' }));

    const load = useCallback(async (keepSetId) => {
        // ARRIVING AT A PARTICULAR SET. `?set=` lets another screen hand you the one it just made — a
        // redaction created from the Universal API tab is empty, and landing on the default instead
        // would leave somebody looking at a set they did not ask for while theirs strikes nothing.
        const wanted = keepSetId
            || new URLSearchParams(window.location.search).get('set')
            || null;
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.getPayload(id);
            const d = res.data || res;
            setPayload(d);
            const list = d.redaction_sets || [];
            setSets(list);
            setScopes(d.redaction_scopes || []);
            // The default leads, and is where a first-time visitor lands: it is the baseline every other
            // set is a departure from.
            const chosen = list.find(x => String(x.id) === String(wanted))
                || list.find(x => x.is_default === 'Y')
                || list[0];
            setActiveSetId(chosen ? chosen.id : null);
            // Returning to a set after a save or a create keeps you in it; a plain load lands on the list.
            setView(wanted && list.some(x => String(x.id) === String(wanted)) ? 'edit' : 'list');
            const mine = toRules(d.redactions, chosen?.id);
            setRules(mine);
            setSaved(mine);
        } catch (e) {
            setError(errText(e, 'Could not load that payload'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Independent of the payload: a failure here costs the picker, not the screen.
    useEffect(() => {
        legacyUapiService.listRedactionTargets()
            .then(res => setTargets(res.data || res))
            .catch(() => setTargets({ partners: [], accounts: [], hotels: [], access: [] }));
    }, []);

    const activeSet = useMemo(
        () => sets.find(x => String(x.id) === String(activeSetId)) || null, [sets, activeSetId]);
    // NOT EDITABLE HERE. The default is nobody's to change — it is the baseline a caller gets when
    // nothing else matches — and on the property side a system set belongs to the platform. Both end up
    // in `locked`, because every control that asks "may I write" asks the same question.
    const locked = activeSet?.is_default === 'Y'
        || (propertyMode && !activeSet?.owner_account_id && !activeSet?.owner_hotel_id);
    // A set with no owner is the platform's own. Only those carry an audience: an owned set is pointed
    // at callers through an access profile instead.
    const isSystemSet = !activeSet?.owner_account_id && !activeSet?.owner_hotel_id;
    const ownerLabel = activeSet?.owner_hotel_id
        ? `${activeSet.owner_hotel_name || 'This hotel'}`
        : activeSet?.owner_account_id
            ? `${activeSet.owner_account_name || 'This chain'}`
            : 'The platform';

    const example = useMemo(() => {
        if (!payload?.example_json) return null;
        try { return JSON.parse(payload.example_json); } catch { return null; }
    }, [payload]);

    const strip = (list) => list.map(({ name, json_path, mode, replacement, scope }) =>
        ({ name, json_path, mode, replacement, scope }));

    // Where else the shape under a given path appears. type_locations comes from the server with the
    // payload — the schema is the authority on which type owns which path — so the reach of a shape rule
    // is stated BEFORE it is saved rather than discovered afterwards.
    // Asked only for the rule being edited, and only once it is a shape rule with a field: the answer
    // costs a scan of every payload that declares the type, and it is a decision aid, not a display.
    const coverageKey = (type, path) => `${type}::${path || ''}`;
    const askCoverage = useCallback((type, path) => {
        const key = coverageKey(type, path);
        setCoverage(prev => (prev[key] ? prev : { ...prev, [key]: { loading: true } }));
        legacyUapiService.redactionCoverage(type, path)
            .then(res => setCoverage(prev => ({ ...prev, [key]: res.data || res })))
            .catch(() => setCoverage(prev => ({ ...prev, [key]: { failed: true } })));
    }, []);

    const shapeReach = (path) => {
        const locs = payload?.type_locations || {};
        let best = null;
        for (const [type, paths] of Object.entries(locs)) {
            for (const at of paths) {
                if (at === path || (at && path.startsWith(at + '.'))) {
                    if (!best || at.length > best.at.length) best = { type, at, places: paths.length };
                }
            }
        }
        return best;
    };
    // The open rule, when it is a shape rule with a field — the only one whose reach is worth the scan.
    const openShape = useMemo(() => {
        const r = rules.find(x => x.key === openRule);
        if (!r || (r.scope || 'PATH') !== 'TYPE' || !r.json_path) return null;
        const reach = shapeReach(r.json_path);
        if (!reach) return null;
        return {
            type: reach.type,
            within: reach.at === r.json_path ? '' : r.json_path.slice(reach.at.length + 1),
        };
        // shapeReach reads only `payload`, which is in the list — it is re-derived on every render and
        // naming it as a dependency would recompute this on every keystroke.
    }, [rules, openRule, payload]);

    // Asked here rather than while rendering: a fetch that sets state during render re-enters the render
    // it was called from.
    useEffect(() => {
        if (!openShape) return;
        const key = coverageKey(openShape.type, openShape.within);
        if (coverage[key]) return;
        askCoverage(openShape.type, openShape.within);
    }, [openShape, coverage, askCoverage]);

    const dirty = useMemo(
        () => JSON.stringify(strip(rules)) !== JSON.stringify(strip(saved)), [rules, saved]);

    const patch = (key, changes) =>
        setRules(prev => prev.map(r => (r.key === key ? { ...r, ...changes } : r)));

    // Switching sets discards nothing silently: unsaved work on the set being left would simply vanish.
    // DUPLICATE — a copy of a set, rules and all, to depart from.
    //
    // THE COPY BELONGS WHERE YOU ARE. From Admin it is another system set; from the Universal API side
    // it belongs to the chain or hotel in the nav bar. EITHER KIND can be copied — a property departing
    // from the platform's version is the common case, and departing from its own earlier one is the
    // next most common.
    const duplicateSet = async (set) => {
        setDuplicating(set.id);
        try {
            // "hotelid (copy)" rather than a blank to fill in: the copy exists to be edited, and naming
            // it is the least interesting part of that. One name per document is a unique key, so the
            // suffix grows until it is free rather than failing on the second copy.
            const taken = new Set(sets.map(x => String(x.name).toLowerCase()));
            let name = `${set.name} (copy)`;
            for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${set.name} (copy ${n})`;
            const res = await legacyUapiService.duplicateRedactionSet(set.id, {
                name,
                hotelId: propertyMode ? (selectedHotel || null) : null,
                accountId: propertyMode && !selectedHotel ? (selectedAccount || null) : null,
            });
            await load();
            const created = (res.data || res).set;
            if (created) chooseSet(created);
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not duplicate that redaction'));
        } finally {
            setDuplicating(null);
        }
    };

    const chooseSet = (set) => {
        if (String(set.id) === String(activeSetId)) { setView('edit'); return; }
        if (dirty) { setConfirmLeave({ kind: 'switch', set }); return; }
        applySet(set);
    };

    const applySet = (set) => {
        setActiveSetId(set.id);
        setDraft({ partner_id: '', account_id: '', hotel_id: '', uapi_access_id: '' });
        setView('edit');
        setRuleSearch('');
        setOpenRule(null);
        const mine = toRules(payload?.redactions, set.id);
        setRules(mine);
        setSaved(mine);
        setPicking(null);
        setError(null);
    };

    const ownerOf = (x) => (!x.owner_account_id && !x.owner_hotel_id ? 'system'
        : x.owner_hotel_id ? 'hotel' : 'chain');

    // MAY THIS SCREEN CHANGE THAT SET. One rule, asked by every control that writes — the row's Delete
    // and the editor's Save were asking different questions, and the row was the one that said yes to a
    // platform set on a property's screen.
    const canEdit = (x) => (propertyMode ? ownerOf(x) !== 'system' : ownerOf(x) === 'system');

    // WHAT THIS SCREEN IS ALLOWED TO SHOW. In Admin, the platform's own and nothing else. On the
    // property side, the platform's own plus whatever this chain and this hotel own — a hotel sees its
    // chain's sets because they apply above it, and seeing only its own would hide half the answer.
    const visibleSets = useMemo(() => sets.filter(x => {
        const owner = ownerOf(x);
        if (!propertyMode) return owner === 'system';
        if (owner === 'system') return true;
        if (x.owner_hotel_id) return String(x.owner_hotel_id) === String(selectedHotel);
        return String(x.owner_account_id) === String(selectedAccount);
    }), [sets, propertyMode, selectedHotel, selectedAccount]);

    const shownSets = visibleSets.filter(x => {
        const q = setSearch.trim().toLowerCase();
        if (!q) return true;
        const mine = scopes.filter(sc => String(sc.redaction_set_id) === String(x.id));
        return [x.name, x.description,
                ...mine.flatMap(sc => [sc.partner_name, sc.account_name, sc.hotel_name, sc.x_hotelid])]
            .some(v => String(v || '').toLowerCase().includes(q));
    });

    // Filtered by name, field or mode. Kept out of `rules` itself so a search can never cause a save to
    // write only the rules that happened to match — the list is a view, the set is the truth.
    const shownRules = rules.filter(r => {
        const q = ruleSearch.trim().toLowerCase();
        if (!q) return true;
        return [r.name, r.json_path, r.mode, r.replacement]
            .some(v => String(v || '').toLowerCase().includes(q));
    });

    const add = () => {
        if (locked) return;
        const rule = newRule();
        setRules(prev => [...prev, rule]);
        setOpenRule(rule.key);  // a new rule has nothing to show collapsed
        setPicking(rule.key);   // a rule with no field is not a rule yet
    };

    const remove = (key) => {
        setRules(prev => prev.filter(r => r.key !== key));
        if (picking === key) setPicking(null);
    };

    const chooseField = (path, leafName) => {
        if (locked) return;
        if (picking) {
            // The leaf name seeds an unnamed rule and never overwrites one the user typed - re-picking
            // the field corrects the path, not the name.
            setRules(prev => prev.map(r =>
                (r.key === picking ? { ...r, json_path: path, name: r.name || leafName } : r)));
            setOpenRule(picking);
            setPicking(null);
            return;
        }
        // Already covered: open the rule that covers it rather than doing nothing, which reads as the
        // click having missed.
        const existing = rules.find(r => r.json_path === path);
        if (existing) { setOpenRule(existing.key); return; }
        const rule = { ...newRule(), json_path: path, name: leafName };
        setRules(prev => [...prev, rule]);
        setOpenRule(rule.key);
    };

    // The scopes of one set.
    const scopesOf = (setId) => scopes.filter(sc => String(sc.redaction_set_id) === String(setId));

    // The cascade. Each level offers only what sits under the one above it, the way the navbar narrows —
    // so a chain that belongs to another partner is never on offer, and the server's consistency check
    // never has to refuse something the screen allowed.
    const cascade = {
        partners: targets ? (targets.partners || []) : [],
        accounts: (targets ? (targets.accounts || []) : [])
            .filter(a => String(a.partnerid) === String(draft.partner_id)),
        hotels: (targets ? (targets.hotels || []) : [])
            .filter(h => String(h.account_id) === String(draft.account_id)),
        access: (targets ? (targets.access || []) : [])
            .filter(x => String(x.hotel_id) === String(draft.hotel_id)),
    };

    // Choosing at one level clears everything below it: the old chain is not under the new partner.
    const pick = (level, value) => setDraft(prev => {
        const next = { ...prev };
        const order = ['partner_id', 'account_id', 'hotel_id', 'uapi_access_id'];
        const at = order.indexOf(level);
        next[level] = value;
        for (const c of order.slice(at + 1)) next[c] = '';
        return next;
    });

    // Saved on its own, not with the rules. The rules Save replaces the whole rule list; an audience is
    // one row, and one button meaning both would be two decisions in one click.
    const addScope = async () => {
        if (locked || !activeSet || !draft.partner_id) return;
        setSavingScope(true);
        setError(null);
        try {
            await legacyUapiService.addRedactionScope(activeSet.id, draft);
            setDraft({ partner_id: '', account_id: '', hotel_id: '', uapi_access_id: '' });
            setNotice(`"${activeSet.name}" now covers one more audience.`);
            await load(activeSet.id);
        } catch (e) {
            setError(errText(e, 'Could not add that audience'));
        } finally {
            setSavingScope(false);
        }
    };

    const removeScope = async (scope) => {
        setSavingScope(true);
        setError(null);
        try {
            await legacyUapiService.deleteRedactionScope(scope.id);
            const read = scopeReads(scope);
            setNotice(`"${activeSet.name}" no longer covers ${read.name || read.level}.`);
            await load(activeSet.id);
        } catch (e) {
            setError(errText(e, 'Could not remove that audience'));
        } finally {
            setSavingScope(false);
        }
    };

    const createSet = async () => {
        const name = newName.trim();
        if (!name) return;
        try {
            // Admin makes the platform's; the property side makes its own. Same button, and the owner
            // is where you are rather than a question asked twice.
            const res = await legacyUapiService.createRedactionSet(id, {
                name,
                hotelId: propertyMode ? (selectedHotel || null) : null,
                accountId: propertyMode && !selectedHotel ? (selectedAccount || null) : null,
            });
            const set = (res.data || res).set;
            setAdding(false);
            setNewName('');
            setNotice(`Redaction "${set.name}" added.`);
            await load(set.id);
        } catch (e) {
            setError(errText(e, 'Could not add that redaction'));
        }
    };

    const removeSet = async () => {
        const set = deleting;
        setDeleting(null);
        try {
            await legacyUapiService.deleteRedactionSet(set.id);
            setNotice(`Redaction "${set.name}" deleted.`);
            await load();
        } catch (e) {
            setError(errText(e, 'Could not delete that redaction'));
        }
    };

    // RENAMING THE SET. Edited in place on the heading rather than in a dialog: a name is one field,
    // and the thing being named is on screen behind the dialog that would have covered it.
    //
    // SAVED ON BLUR AND ON ENTER, not behind a button of its own. The rules below have a Save because
    // they are a list somebody is midway through editing; a name is finished the moment you stop typing.
    const [renaming, setRenaming] = useState(null);   // the text being typed, or null when not editing

    const commitName = async () => {
        const next = String(renaming ?? '').trim();
        setRenaming(null);
        if (!next || !activeSet || next === activeSet.name) return;
        try {
            await legacyUapiService.renameRedactionSet(activeSet.id, { name: next });
            await load(activeSet.id);
            setNotice(`Renamed to “${next}”.`);
        } catch (e) {
            // A name is unique per document, so the usual failure is a name already taken — and the
            // message from the server says which, where "could not save" would not.
            setError(apiErrorMessage(e, 'Could not rename that redaction'));
        }
    };

    const save = async () => {
        const unnamed = rules.find(r => !r.name.trim());
        if (unnamed) { setError('Every rule needs a name.'); return; }
        const pathless = rules.find(r => !r.json_path);
        if (pathless) {
            setError(`"${pathless.name}" has no field yet — click Choose field, then a field in the document.`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await legacyUapiService.setRedactions(activeSetId, strip(rules));
            setNotice(`"${activeSet.name}" saved — ${rules.length} rule${rules.length === 1 ? '' : 's'}.`);
            await load(activeSetId);
        } catch (e) {
            setError(errText(e, 'Could not save those rules'));
        } finally {
            setSaving(false);
        }
    };

    const leave = () => navigate(ORIGINS[from] || '/admin/universal-api/payloads');

    // Back means one step out: out of a redaction to the list, out of the list to wherever you came in.
    const back = () => {
        if (view === 'edit') {
            if (dirty) { setConfirmLeave({ kind: 'list' }); return; }
            setView('list');
            return;
        }
        leave();
    };

    const byPath = useMemo(() => {
        const m = {};
        for (const r of rules) if (r.json_path) m[r.json_path] = r;
        return m;
    }, [rules]);

    const inheritedFrom = (path) => {
        const parts = path.split('.');
        for (let i = parts.length - 1; i > 0; i--) {
            const parent = parts.slice(0, i).join('.');
            if (byPath[parent]?.mode === 'NODE') return parent;
        }
        return null;
    };

    // ── the document ────────────────────────────────────────────────────────
    const renderNode = (value, path, key, depth) => {
        const rule = byPath[path];
        const inherited = !rule && inheritedFrom(path);
        const cls = `uapi-rd-node${rule ? ` redacted redacted-${rule.mode.toLowerCase()}` : ''}`
            + `${inherited ? ' inherited' : ''}${picking ? ' picking' : ''}${locked ? ' locked' : ''}`;

        const label = (
            <button
                type="button"
                className={cls}
                onClick={(e) => { e.stopPropagation(); if (!inherited) chooseField(path, key); }}
                title={locked
                    ? 'The default redaction removes nothing'
                    : inherited
                        ? `Removed by "${byPath[inherited].name}"`
                        : rule ? `Redacted by "${rule.name}"` : 'Click to redact this field'}
            >
                <span className="uapi-rd-key">{key}</span>
                {rule && <span className={`uapi-rd-badge ${rule.mode.toLowerCase()}`}>
                    {rule.mode === 'NODE' ? 'removed' : (rule.replacement || DEFAULT_REPLACEMENT)}
                </span>}
            </button>
        );

        if (Array.isArray(value)) {
            return (
                <div className="uapi-rd-row" key={path} style={{ paddingLeft: depth * 14 }}>
                    {label}<span className="uapi-rd-punct">: [</span>
                    {/* One element, because one is all the paths address — indices collapse to []. */}
                    {value.length > 0 && renderValue(value[0], `${path}[]`, depth + 1)}
                    <span className="uapi-rd-punct" style={{ paddingLeft: depth * 14 }}>]</span>
                </div>
            );
        }
        if (value && typeof value === 'object') {
            return (
                <div className="uapi-rd-row" key={path} style={{ paddingLeft: depth * 14 }}>
                    {label}<span className="uapi-rd-punct">: {'{'}</span>
                    {Object.entries(value).map(([k, v]) => renderNode(v, `${path}.${k}`, k, depth + 1))}
                    <span className="uapi-rd-punct" style={{ paddingLeft: depth * 14 }}>{'}'}</span>
                </div>
            );
        }
        return (
            <div className="uapi-rd-row" key={path} style={{ paddingLeft: depth * 14 }}>
                {label}<span className="uapi-rd-punct">: </span>
                <span className="uapi-rd-value">{JSON.stringify(value)}</span>
            </div>
        );
    };

    const renderValue = (value, path, depth) => {
        if (Array.isArray(value)) return value.length ? renderValue(value[0], `${path}[]`, depth) : null;
        if (value && typeof value === 'object') {
            return Object.entries(value).map(([k, v]) => renderNode(v, `${path}.${k}`, k, depth));
        }
        return <span className="uapi-rd-value" style={{ paddingLeft: depth * 14 }}>{JSON.stringify(value)}</span>;
    };

    return (
        <div className="otx-page">
            <div className="otx-header">
                <div>
                    <h1>Redactions</h1>
                    <p className="otx-subtitle">
                        {payload
                            ? <>Named views of <strong>{payload.name || payload.operation_path}</strong>. A consumer is
                               given one; the default passes the document through untouched.</>
                            : 'Loading...'}
                    </p>
                </div>
                <div className="otx-header-actions">
                    <Button variant="secondary"
                            onClick={() => (view === 'list' && dirty ? setConfirmLeave({ kind: 'leave' }) : back())}>
                        <ArrowLeft className="icon" /> {view === 'edit' ? 'Redactions' : 'Back'}
                    </Button>
                    {/* Nothing to save on the list — the Save button was there and permanently disabled,
                        which reads as broken rather than as not applicable. */}
                    {view === 'edit' && (
                        <Button variant="success" onClick={save} disabled={saving || loading || !dirty || locked}>
                            <Save className="icon" /> {saving ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                </div>
            </div>

            {notice && <p className="lu-payload-notice">{notice}</p>}

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {confirmLeave && (
                <ConfirmModal
                    title={confirmLeave.kind === 'switch' ? 'Switch redaction without saving?' : 'Leave without saving?'}
                    message={`The rules you changed in "${activeSet?.name}" have not been saved and will be lost.`}
                    confirmLabel="Discard changes"
                    danger
                    onConfirm={() => {
                        const c = confirmLeave;
                        setConfirmLeave(null);
                        if (c.kind === 'switch') applySet(c.set);
                        else if (c.kind === 'list') { setRules(saved); setView('list'); }
                        else leave();
                    }}
                    onCancel={() => setConfirmLeave(null)}
                />
            )}

            {deleting && (
                <ConfirmModal
                    title="Delete this redaction?"
                    message={`"${deleting.name}" and its ${deleting.rule_count || 0} rule(s) will be removed. `
                        + 'Any consumer using it falls back to the default, which redacts nothing.'}
                    confirmLabel="Delete"
                    danger
                    onConfirm={removeSet}
                    onCancel={() => setDeleting(null)}
                />
            )}

            {loading ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading payload...</p></div>
            ) : (
                <>
                    {/* THE REDACTIONS. A list, because a payload can carry one per consumer and a strip
                        of pills has nowhere to say what each is for, how many rules it holds, or which is
                        the baseline. Click one to open it. */}
                    {view === 'list' ? (
                        <div className="lu-panel uapi-rd-sets-panel">
                            {/* WHOSE LIST THIS IS, said once at the top. The two screens are otherwise
                                identical, and "why can I not edit this" is answered here rather than
                                discovered on a disabled button. */}
                            <p className="uapi-rd-mode-note">
                                {propertyMode
                                    ? 'System redactions are the platform’s and are shown here read-only. '
                                      + 'Add your own, or duplicate any of them, to depart from one.'
                                    : 'The platform’s own redactions. A chain’s or a hotel’s are made and '
                                      + 'edited from Universal API, Redactions.'}
                            </p>
                            <div className="lu-panel-header">
                                <h2>Redactions ({visibleSets.length})</h2>
                                {visibleSets.length > 4 && (
                                    <div className="uapi-search-row uapi-rd-search">
                                        <Search size={14} className="uapi-search-icon" />
                                        <input
                                            type="text"
                                            className="pst-hotel-search"
                                            placeholder="Search redactions…"
                                            value={setSearch}
                                            onChange={e => setSetSearch(e.target.value)}
                                        />
                                        <span className="lu-muted">{shownSets.length} of {visibleSets.length}</span>
                                    </div>
                                )}
                                {!adding && (!propertyMode || selectedAccount) && (
                                    <button type="button" className="pp-rules-add-btn" onClick={() => setAdding(true)}>
                                        {/* Named by WHOSE it will be. The same button on two screens
                                            making two different things, with only the heading to tell
                                            them apart, is how a system redaction gets written by
                                            somebody who meant to write their own. */}
                                        <Plus size={14} />
                                        {propertyMode
                                            ? (selectedHotel ? 'Add for this hotel' : 'Add for this chain')
                                            : 'Add system redaction'}
                                    </button>
                                )}
                            </div>

                            {adding && (
                                <div className="uapi-rd-set-new-row">
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="Name this redaction — who is it for?"
                                        value={newName}
                                        autoFocus
                                        maxLength={255}
                                        onChange={e => setNewName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') createSet();
                                            if (e.key === 'Escape') { setAdding(false); setNewName(''); }
                                        }}
                                    />
                                    <button type="button" className="pp-rules-add-btn" onClick={createSet}
                                            disabled={!newName.trim()}>
                                        Add
                                    </button>
                                    <button type="button" className="ppp-detail-btn"
                                            onClick={() => { setAdding(false); setNewName(''); }}>
                                        Cancel
                                    </button>
                                </div>
                            )}

                            <div className="uapi-rd-set-list">
                                {shownSets.length === 0 && (
                                    <p className="lu-empty">No redaction matches that.</p>
                                )}
                                {shownSets.map(set => (
                                    <div key={set.id} className={`uapi-rd-set-row${set.is_default === 'Y' ? ' default' : ''}`}>
                                        <button type="button" className="uapi-rd-set-open"
                                                onClick={() => chooseSet(set)}>
                                            {set.is_default === 'Y' ? <Lock size={14} /> : <Shield size={14} />}
                                            <span className="uapi-rd-set-name">{set.name}</span>
                                            {/* Marked, not inferred from a blank: whether a set is the
                                                platform's decides whether this screen may change it. */}
                                            <span className={`uapi-redaction-owner${ownerOf(set) === 'system' ? ' system' : ''}`}>
                                                {ownerOf(set) === 'system' ? 'System'
                                                    : ownerOf(set) === 'hotel'
                                                        ? (set.owner_hotel_name || 'This hotel')
                                                        : (set.owner_account_name || 'This chain')}
                                            </span>
                                            {set.is_default === 'Y' ? (
                                                <span className="uapi-system-chip">baseline · redacts nothing</span>
                                            ) : scopesOf(set.id).length ? (
                                                /* WHO GETS IT — the reason the set exists, so it sits on
                                                   the row rather than inside the editor only. */
                                                <span className="uapi-scope-chips">
                                                    {scopesOf(set.id).slice(0, 2).map(sc => {
                                                        const r = scopeReads(sc);
                                                        return (
                                                            <span key={sc.id} className="uapi-target-chip"
                                                                  title={r.trail.filter(Boolean).join(' › ')}>
                                                                <em>{r.level}</em>
                                                                {r.name || `#${sc.match_id}`}
                                                            </span>
                                                        );
                                                    })}
                                                    {scopesOf(set.id).length > 2 && (
                                                        <span className="uapi-target-chip more">
                                                            +{scopesOf(set.id).length - 2}
                                                        </span>
                                                    )}
                                                </span>
                                            ) : (
                                                /* Addressed to nobody reaches nobody. Said plainly: a set
                                                   of rules that looks configured and applies to no one is
                                                   the failure this feature is for. */
                                                <span className="uapi-target-chip none">
                                                    <Users size={10} /> not addressed
                                                </span>
                                            )}
                                            <span className="lu-muted uapi-rd-set-desc">{set.description || ''}</span>
                                            <span className="uapi-rd-set-count">
                                                {set.rule_count || 0} rule{Number(set.rule_count) === 1 ? '' : 's'}
                                            </span>
                                        </button>
                                        {/* DUPLICATE IS OFFERED ON EVERY SET, the default included: the
                                            baseline redacts nothing, and "everything except one field"
                                            is a reasonable thing to start from. */}
                                        <button
                                            type="button"
                                            className="ppp-detail-btn"
                                            onClick={() => duplicateSet(set)}
                                            disabled={duplicating === set.id || (propertyMode && !selectedAccount)}
                                            title={propertyMode && !selectedAccount
                                                ? 'Choose a chain in the navigation bar first — a copy belongs to one'
                                                : `Make a copy of "${set.name}" to edit`}
                                        >
                                            <Copy size={12} /> {duplicating === set.id ? 'Copying…' : 'Duplicate'}
                                        </button>
                                        {/* The default cannot be deleted: it is what a consumer with no
                                            redaction assigned receives. Nor can a set this screen does
                                            not own — a property deleting the platform's redaction would
                                            take it from every other property too. */}
                                        {set.is_default !== 'Y' && canEdit(set) && (
                                            <button
                                                type="button"
                                                className="ppp-detail-btn ppp-detail-btn-error"
                                                onClick={() => setDeleting(set)}
                                                title={`Delete "${set.name}"`}
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : !example ? (
                        <div className="otx-table-wrap uapi-empty-state">
                            <p className="uapi-empty-title">No example document to work from.</p>
                            <p className="uapi-empty-body">
                                {payload?.spec_file} publishes no example for this payload and one could not be
                                built from its schema. Re-import the payload, or check the schema parses.
                            </p>
                        </div>
                    ) : (
                        <div className="lu-layout uapi-rd-layout">
                            <div className="lu-panel uapi-rd-list-panel">
                                <div className="lu-panel-header">
                                    {/* The DEFAULT cannot be renamed: "Default" is what the screens
                                        call the baseline everywhere, and a renamed one would read as an
                                        ordinary set while still behaving as the fallback. */}
                                    {locked || renaming === null ? (
                                        <h2
                                            className={locked ? '' : 'uapi-rd-name-editable'}
                                            onClick={() => { if (!locked) setRenaming(activeSet?.name || ''); }}
                                            title={locked ? undefined : 'Click to rename'}
                                        >
                                            {activeSet?.name} ({rules.length})
                                        </h2>
                                    ) : (
                                        <input
                                            className="form-input uapi-rd-name-input"
                                            value={renaming}
                                            autoFocus
                                            onChange={(e) => setRenaming(e.target.value)}
                                            onBlur={commitName}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') { e.preventDefault(); commitName(); }
                                                // Escape abandons the edit rather than saving what was
                                                // half-typed — the one key a reader expects to undo.
                                                if (e.key === 'Escape') setRenaming(null);
                                            }}
                                        />
                                    )}
                                    {dirty && <span className="uapi-override-none">unsaved</span>}
                                    {!locked && (
                                        <button type="button" className="pp-rules-add-btn" onClick={add}>
                                            <Plus size={14} /> Add rule
                                        </button>
                                    )}
                                    {!locked && (
                                        <button
                                            type="button"
                                            className="ppp-detail-btn ppp-detail-btn-error"
                                            onClick={() => setDeleting(activeSet)}
                                            title="Delete this redaction"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>

                                {/* ADDRESSED TO — SYSTEM SETS ONLY.
                                    The precedence is stated, not implied: a hotel rule beats a chain rule
                                    beats a partner rule, and an x-hotelid rule beats them all. Saved on
                                    its own — the rules Save below replaces the whole rule list, and one
                                    button meaning both would be two decisions in one click.

                                    AN OWNED SET IS NOT ADDRESSED HERE. A chain's or a hotel's redaction
                                    is applied by naming it on a call of an access profile, which is where
                                    that chain or hotel's access is decided in the first place. Two ways
                                    to point the same redaction at the same caller is two answers to
                                    "why is this field struck", and the rule is that there is one. */}
                                {!locked && isSystemSet && (
                                    <div className="uapi-rd-target">
                                        <span className="uapi-rd-target-label">
                                            <Users size={13} /> Applies to
                                        </span>

                                        {/* WHAT IT ALREADY COVERS. A redaction can be given as many
                                            audiences as it needs — the same "no passport numbers" view is
                                            wanted for two chains and a handful of properties, and one
                                            audience per set would mean duplicating the rule list, which
                                            is how two copies drift apart and one stops protecting. */}
                                        {scopesOf(activeSet?.id).length === 0 ? (
                                            <p className="uapi-rd-target-hint none">
                                                Nobody yet. Until an audience is added, this redaction is
                                                written but never applied.
                                            </p>
                                        ) : (
                                            <ul className="uapi-scope-list">
                                                {scopesOf(activeSet.id).map(sc => {
                                                    const r = scopeReads(sc);
                                                    return (
                                                        <li key={sc.id}>
                                                            <span className="uapi-scope-level">{r.level}</span>
                                                            <strong>{r.name || `#${sc.match_id}`}</strong>
                                                            {/* The trail it was chosen by, so a hotel
                                                                named twice across two chains is legible. */}
                                                            {r.trail.filter(Boolean).length > 0 && (
                                                                <span className="uapi-scope-trail">
                                                                    {r.trail.filter(Boolean).join(' › ')}
                                                                </span>
                                                            )}
                                                            {r.covers && (
                                                                <span className="uapi-scope-covers">
                                                                    {r.covers}
                                                                </span>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="ppp-detail-btn ppp-detail-btn-error"
                                                                onClick={() => removeScope(sc)}
                                                                disabled={savingScope}
                                                                title="Stop covering this audience"
                                                            >
                                                                <Trash2 size={11} />
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}

                                        {/* THE CASCADE, the same narrowing as the navbar: a partner, then
                                            a chain within it, then a hotel within that, then one of that
                                            hotel's x-hotelids. Stop wherever it is specific enough —
                                            partner and chain alone means every hotel in the chain. */}
                                        <div className="uapi-rd-target-row">
                                            {[['partner_id', 'Partner', cascade.partners,
                                               o => o.name, null],
                                              ['account_id', 'Chain', cascade.accounts,
                                               o => o.name, 'partner_id'],
                                              ['hotel_id', 'Hotel', cascade.hotels,
                                               o => o.name, 'account_id'],
                                              ['uapi_access_id', 'x-hotelid', cascade.access,
                                               o => `${o.x_hotelid}${o.system_name ? ` — ${o.system_name}` : ''}`,
                                               'hotel_id']].map(([col, label, options, text, needs]) => (
                                                <select
                                                    key={col}
                                                    className="form-input uapi-rd-target-level"
                                                    value={draft[col]}
                                                    disabled={savingScope || !targets || (needs && !draft[needs])}
                                                    onChange={e => pick(col, e.target.value)}
                                                >
                                                    <option value="">
                                                        {needs && !draft[needs]
                                                            ? label
                                                            : `${label}${col === 'partner_id' ? '' : ' (all)'}`}
                                                    </option>
                                                    {options.map(o => (
                                                        <option key={o.id} value={o.id}>{text(o)}</option>
                                                    ))}
                                                </select>
                                            ))}
                                            <button
                                                type="button"
                                                className="pp-rules-add-btn"
                                                onClick={addScope}
                                                disabled={savingScope || !draft.partner_id}
                                            >
                                                <Plus size={13} /> Add
                                            </button>
                                            {savingScope && <Spinner size="sm" />}
                                        </div>
                                        <p className="uapi-rd-target-hint">
                                            Stop at whichever level is specific enough. The narrowest
                                            audience wins: x-hotelid, then hotel, then chain, then partner,
                                            then the default.
                                        </p>
                                    </div>
                                )}

                                {/* An owned set says how it is applied, rather than showing nothing where
                                    the audience picker used to be — a blank would read as a set that
                                    reaches nobody, which is what the picker was there to prevent. */}
                                {!locked && !isSystemSet && (
                                    <div className="uapi-rd-target">
                                        <span className="uapi-rd-target-label">
                                            <Users size={13} /> Applies to
                                        </span>
                                        <p className="uapi-rd-target-hint" style={{ marginTop: '0.35rem' }}>
                                            {ownerLabel} owns this redaction. It is applied by naming it on
                                            a call in an access profile, under Universal API, Access
                                            Profiles — the same place that chain or hotel's access is
                                            granted.
                                        </p>
                                    </div>
                                )}

                                {(payload?.inherited_type_redactions || []).filter(t => !t.own).length > 0 && (
                            <p className="uapi-rd-inherited">
                                <Layers size={13} />
                                <span>
                                    {payload.inherited_type_redactions.filter(t => !t.own).length} field(s) here
                                    are already covered by shape rules written on{' '}
                                    {[...new Set(payload.inherited_type_redactions.filter(t => !t.own)
                                        .map(t => t.authored_on_payload_name))].join(', ')}.
                                    They apply to this document too — there is no need to repeat them.
                                </span>
                            </p>
                        )}

                        {locked ? (
                                    <div className="uapi-rd-default-note">
                                        <Lock size={14} />
                                        <span>
                                            <strong>Nothing is removed or redacted.</strong>
                                            This is what a consumer receives when no redaction has been assigned to
                                            it, so it stays empty — add a redaction of your own to strip fields.
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        {picking && (
                                            <p className="uapi-rd-picking-hint">
                                                <Crosshair size={13} /> Click a field in the document.
                                                <button type="button" className="ppp-detail-btn"
                                                        onClick={() => setPicking(null)}>Cancel</button>
                                            </p>
                                        )}

                                        {rules.length === 0 ? (
                                            <p className="lu-empty">
                                                No rules yet. Add one, or click a field in the document to start
                                                there.
                                            </p>
                                        ) : (
                                            <>
                                            {rules.length > 3 && (
                                                <div className="uapi-search-row uapi-rd-search">
                                                    <Search size={14} className="uapi-search-icon" />
                                                    <input
                                                        type="text"
                                                        className="pst-hotel-search"
                                                        placeholder="Search rules by name or field…"
                                                        value={ruleSearch}
                                                        onChange={e => setRuleSearch(e.target.value)}
                                                    />
                                                    <span className="lu-muted">
                                                        {shownRules.length} of {rules.length}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="uapi-rd-rules">
                                                {shownRules.length === 0 && (
                                                    <p className="lu-empty">No rule matches that.</p>
                                                )}
                                                {shownRules.map(r => {
                                                  const isOpen = openRule === r.key;
                                                  return (
                                                    <div key={r.key}
                                                         className={`uapi-rd-rule${picking === r.key ? ' picking' : ''}`
                                                             + `${isOpen ? ' open' : ''}`}>
                                                    {/* COLLAPSED: everything needed to recognise the rule,
                                                        on one line. The editor only opens for the one being
                                                        changed, so a set of thirty rules can be read. */}
                                                    {!isOpen ? (
                                                        <button
                                                            type="button"
                                                            className="uapi-rd-rule-summary"
                                                            onClick={() => setOpenRule(r.key)}
                                                        >
                                                            <span className={`uapi-rd-badge ${r.mode.toLowerCase()}`}>
                                                                {r.mode === 'NODE' ? 'removed' : (r.replacement || DEFAULT_REPLACEMENT)}
                                                            </span>
                                                            <span className="uapi-rd-summary-name">
                                                                {r.name || <em>unnamed</em>}
                                                            </span>
                                                            {(r.scope || 'PATH') === 'TYPE' && (
                                                                <span className="uapi-shared-chip"><Layers size={10} /> shape</span>
                                                            )}
                                                            <code className="lu-mono uapi-rd-summary-path">
                                                                {r.json_path || 'no field chosen'}
                                                            </code>
                                                        </button>
                                                    ) : (<>
                                                        <div className="uapi-rd-rule-head">
                                                            <input
                                                                type="text"
                                                                className="form-input uapi-rd-name"
                                                                placeholder="Name this rule"
                                                                value={r.name}
                                                                maxLength={255}
                                                                onChange={e => patch(r.key, { name: e.target.value })}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="ppp-detail-btn ppp-detail-btn-error"
                                                                onClick={() => remove(r.key)}
                                                                title="Remove this rule"
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>

                                                        {/* Shown, never typed: chosen from the document so it
                                                            cannot be a near-miss that fails silently. */}
                                                        <div className="uapi-rd-rule-field">
                                                            {r.json_path
                                                                ? <code className="lu-mono uapi-payload-path">{r.json_path}</code>
                                                                : <span className="uapi-override-none">No field chosen</span>}
                                                            <button
                                                                type="button"
                                                                className={`ppp-detail-btn${picking === r.key ? ' ppp-detail-btn-primary' : ''}`}
                                                                onClick={() => setPicking(picking === r.key ? null : r.key)}
                                                            >
                                                                <Crosshair size={12} /> {r.json_path ? 'Change field' : 'Choose field'}
                                                            </button>
                                                        </div>

                                                        <div className="uapi-rd-modes">
                                                            {Object.entries(MODES).map(([mode, m]) => (
                                                                <button
                                                                    key={mode}
                                                                    type="button"
                                                                    className={`uapi-rd-mode${r.mode === mode ? ' picked' : ''}`}
                                                                    onClick={() => patch(r.key, {
                                                                        mode,
                                                                        replacement: mode === 'TEXT' ? r.replacement : null,
                                                                    })}
                                                                    title={m.hint}
                                                                >
                                                                    {mode === 'NODE' ? <EyeOff size={12} /> : <Type size={12} />}
                                                                    {m.label}
                                                                    {r.mode === mode && <Check size={12} />}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* HOW FAR IT REACHES. A shape rule follows the type into every
                                            document carrying it — the list and the single view of the
                                            same record being the case this exists for — so the reach is
                                            named and counted here, before Save, not implied. */}
                                        {(() => {
                                            const reach = r.json_path ? shapeReach(r.json_path) : null;
                                            if (!reach) return null;
                                            return (
                                                <>
                                                    <div className="uapi-rd-scope">
                                                    {[['PATH', 'This document only'],
                                                      ['TYPE', `Everywhere ${reach.type} appears`]].map(([sc, label]) => (
                                                        <button
                                                            key={sc}
                                                            type="button"
                                                            className={`uapi-rd-scope-opt${(r.scope || 'PATH') === sc ? ' picked' : ''}`}
                                                            onClick={() => patch(r.key, { scope: sc })}
                                                            title={sc === 'TYPE'
                                                                ? `${reach.places} place(s) in this document, and every other payload carrying the same shape`
                                                                : 'Just the field at this path, in this document'}
                                                        >
                                                            {sc === 'TYPE' ? <Layers size={11} /> : <Crosshair size={11} />}
                                                            {label}
                                                        </button>
                                                    ))}
                                                    </div>

                                                    {/* WHERE IT ACTUALLY LANDS.
                                                        The rule matches on the type's NAME, and Oracle
                                                        publishes a same-named type in every spec that
                                                        needs one — with different contents. A rule can
                                                        name a field the reservation's copy of the shape
                                                        does not have and match nothing there, silently.
                                                        Resolved against every payload declaring the type,
                                                        and shown before Save. */}
                                                    {(r.scope || 'PATH') === 'TYPE' && (() => {
                                                        const within = reach.at === r.json_path
                                                            ? '' : r.json_path.slice(reach.at.length + 1);
                                                        const key = coverageKey(reach.type, within);
                                                        const cov = coverage[key];
                                                        if (!cov || cov.loading) {
                                                            return <p className="uapi-rd-cover loading">
                                                                Checking where {reach.type} carries this field…
                                                            </p>;
                                                        }
                                                        if (cov.failed) {
                                                            return <p className="uapi-rd-cover">
                                                                Could not check which payloads this reaches.
                                                            </p>;
                                                        }
                                                        const gap = cov.missing || [];
                                                        return (
                                                            <div className={`uapi-rd-cover${gap.length ? ' gap' : ' ok'}`}>
                                                                <p className="uapi-rd-cover-line">
                                                                    {gap.length
                                                                        ? <AlertTriangle size={12} />
                                                                        : <Check size={12} />}
                                                                    <span>
                                                                        Covers <strong>{(cov.covered || []).length}</strong>{' '}
                                                                        payload{(cov.covered || []).length === 1 ? '' : 's'}
                                                                        {gap.length > 0 && <>
                                                                            {' '}· <strong>{gap.length}</strong> carr
                                                                            {gap.length === 1 ? 'ies' : 'y'} {reach.type}
                                                                            {' '}without this field
                                                                        </>}
                                                                    </span>
                                                                </p>
                                                                {gap.length > 0 && (
                                                                    <ul className="uapi-rd-cover-list">
                                                                        {gap.slice(0, 6).map(x => (
                                                                            <li key={x.id}>
                                                                                <strong>{x.name}</strong>
                                                                                <span className="lu-muted">{x.spec_file}</span>
                                                                                <em>no {x.missing_field}</em>
                                                                            </li>
                                                                        ))}
                                                                        {gap.length > 6 && (
                                                                            <li className="lu-muted">
                                                                                and {gap.length - 6} more
                                                                            </li>
                                                                        )}
                                                                    </ul>
                                                                )}
                                                                {gap.length > 0 && (
                                                                    <p className="uapi-rd-cover-hint">
                                                                        Those responses go out with the field intact.
                                                                        Write a rule on each, or redact the whole node
                                                                        instead of one field of it.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            );
                                        })()}

                                        {r.mode === 'TEXT' && (
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder={`Replacement (default ${DEFAULT_REPLACEMENT})`}
                                                                value={r.replacement || ''}
                                                                onChange={e => patch(r.key, { replacement: e.target.value || null })}
                                                            />
                                                        )}

                                                        {/* Done — collapses back to the line. */}
                                                        <button
                                                            type="button"
                                                            className="ppp-detail-btn uapi-rd-close"
                                                            onClick={() => setOpenRule(null)}
                                                        >
                                                            Close
                                                        </button>
                                                    </>)}
                                                    </div>
                                                  );
                                                })}
                                            </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="lu-panel">
                                <div className="lu-panel-header">
                                    <h2>Example document</h2>
                                    <span className="lu-muted">
                                        {payload.example_source === 'generated'
                                            ? 'Built from the schema - a shape, not real data'
                                            : `Published in ${payload.spec_file}`}
                                    </span>
                                </div>
                                <div className={`uapi-rd-doc${picking ? ' picking' : ''}${locked ? ' locked' : ''}`}>
                                    {renderValue(example, '$', 0)}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default UapiPayloadRedactionsPage;
